import React, { useCallback, useRef, useState } from "react";
import {
    downloadBulkProvisionTemplate,
    downloadSkippedRowsFile,
    uploadBulkProvisionFile,
    summarizeBulkValidationErrors,
    countBulkFileDataRows,
    readBulkFileMatrix,
    cellValueForBulkIssue,
    isAcceptedBulkFileName,
    bulkCreatedHeadline,
    bulkSkippedHeadline,
    uniqueFailuresByRow,
    formatSkippedRowLine,
    BULK_FILE_TYPE_MESSAGE,
    BULK_ONE_FILE_MESSAGE,
    BULK_SKIPPED_PREVIEW_LIMIT,
    type BulkProvisionUploadResult,
} from "../services/bulkProvisionService";
import "../styles/add-team-member-modal.css";

type Props = {
    open: boolean;
    onClose: () => void;
    /** Called after athletes are created; may be async (e.g. refetch directory). */
    onProvisioned?: () => void | Promise<void>;
    /** Team view: pre-fill the Admin/Staff/Coach Email Address in the downloaded template (row 2, column E) */
    templateStaffEmail?: string | null;
};

const COLUMN_RULES = [
    { column: "Student / Athlete Email Address", rule: "Valid email, not already registered" },
    { column: "First name", rule: "Letters only, 2–30 characters" },
    { column: "Last name", rule: "Letters only, 2–30 characters" },
    { column: "Parent/Guardian Email Address", rule: "Valid email, different from the athlete's" },
    {
        column: "Admin/Staff/Coach Email Address",
        rule: "An existing Admin, Staff or Coach in your company or school",
    },
] as const;

const FILE_ACCEPT = ".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

function DownloadIcon() {
    return (
        <svg className="bulk-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path
                fill="currentColor"
                d="M10 2.5a.75.75 0 0 1 .75.75v7.19l2.22-2.22a.75.75 0 1 1 1.06 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 0 1 1.06-1.06l2.22 2.22V3.25A.75.75 0 0 1 10 2.5Zm-5.5 11a.75.75 0 0 1 .75.75v1.5h9.5v-1.5a.75.75 0 0 1 1.5 0v2.25c0 .41-.34.75-.75.75h-12a.75.75 0 0 1-.75-.75V14.25a.75.75 0 0 1 .75-.75Z"
            />
        </svg>
    );
}

function UploadIcon() {
    return (
        <svg className="bulk-icon bulk-icon--lg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
                fill="currentColor"
                d="M12 3.25a.75.75 0 0 1 .75.75v9.19l2.72-2.72a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 0 1 1.06-1.06l2.72 2.72V4a.75.75 0 0 1 .75-.75ZM4.5 16.5a.75.75 0 0 1 .75.75v1.5h13.5v-1.5a.75.75 0 0 1 1.5 0V19a1.75 1.75 0 0 1-1.75 1.75H5.5A1.75 1.75 0 0 1 3.75 19v-1.75a.75.75 0 0 1 .75-.75Z"
            />
        </svg>
    );
}

function FileIcon() {
    return (
        <svg className="bulk-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path
                fill="currentColor"
                d="M6 2.75A1.75 1.75 0 0 0 4.25 4.5v11c0 .97.78 1.75 1.75 1.75h8A1.75 1.75 0 0 0 15.75 15.5V7.31c0-.46-.18-.91-.51-1.24l-2.81-2.81A1.75 1.75 0 0 0 11.19 2.75H6Zm5.25 1.81L14.44 7.75H12A.75.75 0 0 1 11.25 7V4.56Z"
            />
        </svg>
    );
}

function ColumnRulesTable() {
    return (
        <table className="bulk-field-table">
            <caption className="bulk-field-table__caption">Required columns and validation rules</caption>
            <tbody>
                {COLUMN_RULES.map((row) => (
                    <tr key={row.column}>
                        <th scope="row">{row.column}</th>
                        <td>{row.rule}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function filesFromDataTransfer(dataTransfer: DataTransfer | null): File[] {
    if (!dataTransfer) {
        return [];
    }
    if (dataTransfer.files && dataTransfer.files.length > 0) {
        return Array.from(dataTransfer.files);
    }
    return Array.from(dataTransfer.items ?? [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file): file is File => file != null);
}

export default function BulkProvisionAthletesModal({
    open,
    onClose,
    onProvisioned,
    templateStaffEmail,
}: Props) {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const fileCountGeneration = useRef(0);
    const [file, setFile] = useState<File | null>(null);
    const [fileRowCount, setFileRowCount] = useState<number | null>(null);
    const [fileMatrix, setFileMatrix] = useState<string[][] | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [validationSummary, setValidationSummary] = useState<string[] | null>(null);
    const [uploadSummary, setUploadSummary] = useState<BulkProvisionUploadResult | null>(null);
    const [rulesOpen, setRulesOpen] = useState(true);
    const [showAllSkipped, setShowAllSkipped] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [skipDownloadError, setSkipDownloadError] = useState<string | null>(null);
    const [rejectedFileName, setRejectedFileName] = useState<string | null>(null);

    const clearSelectedFile = useCallback(() => {
        fileCountGeneration.current += 1;
        setFile(null);
        setFileRowCount(null);
        setFileMatrix(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const reset = useCallback(() => {
        clearSelectedFile();
        setError(null);
        setValidationSummary(null);
        setUploadSummary(null);
        setRulesOpen(true);
        setShowAllSkipped(false);
        setDragActive(false);
        setSkipDownloadError(null);
        setRejectedFileName(null);
    }, [clearSelectedFile]);

    const acceptFile = useCallback((next: File) => {
        setFile(next);
        setError(null);
        setValidationSummary(null);
        setUploadSummary(null);
        setShowAllSkipped(false);
        setSkipDownloadError(null);
        setRejectedFileName(null);
        setRulesOpen(true);
        setFileRowCount(null);
        setFileMatrix(null);
        const generation = ++fileCountGeneration.current;
        void Promise.all([countBulkFileDataRows(next), readBulkFileMatrix(next)]).then(([count, matrix]) => {
            if (fileCountGeneration.current === generation) {
                setFileRowCount(count);
                setFileMatrix(matrix);
            }
        });
    }, []);

    const handleIncomingFiles = useCallback(
        (incoming: File[]) => {
            if (saving || uploadSummary) {
                return;
            }
            if (incoming.length === 0) {
                return;
            }
            if (incoming.length > 1) {
                setError(BULK_ONE_FILE_MESSAGE);
                return;
            }
            const next = incoming[0];
            if (!isAcceptedBulkFileName(next.name)) {
                setError(BULK_FILE_TYPE_MESSAGE);
                return;
            }
            acceptFile(next);
        },
        [acceptFile, saving, uploadSummary]
    );

    if (!open) {
        return null;
    }

    const handleDownloadTemplate = async () => {
        setError(null);
        setSkipDownloadError(null);
        try {
            await downloadBulkProvisionTemplate(templateStaffEmail);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not download template.");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setValidationSummary(null);
        setSkipDownloadError(null);

        if (!file) {
            setError("Please choose a .csv or .xlsx file.");
            return;
        }

        setSaving(true);
        try {
            const result = await uploadBulkProvisionFile(file);
            if ("status" in result) {
                if (result.status === 422 && result.limitMessage) {
                    setError(result.limitMessage);
                } else if (result.status === 400) {
                    const raw = result.validationErrors ?? [];
                    setRejectedFileName(file.name);
                    clearSelectedFile();
                    if (raw.length > 0) {
                        setValidationSummary(summarizeBulkValidationErrors(raw));
                    } else {
                        setValidationSummary([
                            result.detail ??
                                "We could not process this file. Use the Template button, fill in your data, and try again.",
                        ]);
                    }
                } else {
                    if (
                        typeof result.approximateCreated === "number" &&
                        result.approximateCreated > 0
                    ) {
                        await Promise.resolve(onProvisioned?.());
                    }
                    setError(
                        result.detail ??
                            "Upload failed. Please try again, or contact support if the problem continues."
                    );
                }
            } else {
                setUploadSummary(result);
                setRulesOpen(false);
                setShowAllSkipped(false);
                if (result.createdCount > 0) {
                    await Promise.resolve(onProvisioned?.());
                }
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Upload failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            reset();
            onClose();
        }
    };

    const handleUploadAnother = () => {
        if (!saving) {
            reset();
        }
    };

    const handleDownloadSkipped = async () => {
        if (!file || !uploadSummary || uploadSummary.failedRowCount === 0) {
            return;
        }
        setSkipDownloadError(null);
        try {
            await downloadSkippedRowsFile(file, uploadSummary.failures);
        } catch (e: unknown) {
            setSkipDownloadError(
                e instanceof Error ? e.message : "Could not download skipped rows."
            );
        }
    };

    const hasResults = !!uploadSummary;
    const skippedIssues = uploadSummary ? uniqueFailuresByRow(uploadSummary.failures) : [];
    const visibleSkipped = showAllSkipped
        ? skippedIssues
        : skippedIssues.slice(0, BULK_SKIPPED_PREVIEW_LIMIT);
    const createdLine = uploadSummary ? bulkCreatedHeadline(uploadSummary.createdCount) : null;
    const skippedLine = uploadSummary ? bulkSkippedHeadline(uploadSummary.failedRowCount) : null;
    const rowCountLabel =
        fileRowCount == null
            ? null
            : `${fileRowCount} ${fileRowCount === 1 ? "row" : "rows"}`;

    return (
        <div className="team-modal" role="dialog" aria-modal="true" aria-labelledby="bulk-provision-title">
            <div className="team-modal-card team-modal-card--bulk-upload">
                <button className="team-modal-close" type="button" aria-label="Close" onClick={handleClose} disabled={saving}>
                    ×
                </button>
                <div className="team-modal-content bulk-upload-content">
                    <h2 id="bulk-provision-title" className="form-title bulk-upload-title">
                        Bulk upload athletes
                    </h2>
                    <p className="bulk-upload-intro">
                        Add up to 100 athletes at once from a CSV or Excel file.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <ol className="bulk-steps">
                            <li className="bulk-step">
                                <div className="bulk-step__marker" aria-hidden="true">
                                    1
                                </div>
                                <div className="bulk-step__body">
                                    <div className="bulk-step__head">
                                        <div>
                                            <h3 className="bulk-step__title">Download the template</h3>
                                            <p className="bulk-step__copy">
                                                Column headers must match the template exactly.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="bulk-ghost-btn bulk-upload-template-btn"
                                            onClick={handleDownloadTemplate}
                                            disabled={saving}
                                            aria-label="Download template (.xlsx)"
                                        >
                                            <DownloadIcon />
                                            <span>Template (.xlsx)</span>
                                        </button>
                                    </div>
                                </div>
                            </li>

                            <li className="bulk-step">
                                <div className="bulk-step__marker" aria-hidden="true">
                                    2
                                </div>
                                <div className="bulk-step__body">
                                    <div className="bulk-step__head">
                                        <div>
                                            <h3 className="bulk-step__title">Add one athlete per row</h3>
                                            <p className="bulk-step__copy">
                                                Up to 100 rows. Every column is required.
                                                {hasResults && (
                                                    <>
                                                        {" "}
                                                        <button
                                                            type="button"
                                                            className="bulk-text-link"
                                                            onClick={() => setRulesOpen((openRules) => !openRules)}
                                                            aria-expanded={rulesOpen}
                                                        >
                                                            {rulesOpen ? "Hide column rules" : "Show column rules"}
                                                        </button>
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    {(!hasResults || rulesOpen) && <ColumnRulesTable />}
                                </div>
                            </li>

                            <li className="bulk-step">
                                <div className="bulk-step__marker" aria-hidden="true">
                                    3
                                </div>
                                <div className="bulk-step__body">
                                    <h3 className="bulk-step__title">Upload your file</h3>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={FILE_ACCEPT}
                                        className="bulk-upload-file-input"
                                        disabled={saving || hasResults}
                                        aria-label="File (.csv or .xlsx)"
                                        onChange={(ev) => {
                                            handleIncomingFiles(ev.target.files ? Array.from(ev.target.files) : []);
                                            ev.target.value = "";
                                        }}
                                    />

                                    {hasResults && file ? (
                                        <div className="bulk-file-card">
                                            <div className="bulk-file-card__meta">
                                                <FileIcon />
                                                <div>
                                                    <p className="bulk-file-card__name">{file.name}</p>
                                                    <p className="bulk-file-card__sub">
                                                        {rowCountLabel
                                                            ? `${rowCountLabel} · uploaded just now`
                                                            : "Uploaded just now"}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="bulk-ghost-btn"
                                                onClick={handleUploadAnother}
                                                disabled={saving}
                                            >
                                                Upload another
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className={[
                                                "bulk-dropzone",
                                                dragActive ? "bulk-dropzone--active" : "",
                                                rejectedFileName && !file ? "bulk-dropzone--needs-file" : "",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                            aria-label={
                                                file
                                                    ? `Replace file, currently ${file.name}`
                                                    : rejectedFileName
                                                      ? "Choose a fixed file or drag it here"
                                                      : "Choose a file or drag it here"
                                            }
                                            disabled={saving}
                                            onClick={() => fileInputRef.current?.click()}
                                            onDragEnter={(e) => {
                                                e.preventDefault();
                                                if (!saving) {
                                                    setDragActive(true);
                                                }
                                            }}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = "copy";
                                            }}
                                            onDragLeave={(e) => {
                                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                                    setDragActive(false);
                                                }
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragActive(false);
                                                handleIncomingFiles(filesFromDataTransfer(e.dataTransfer));
                                            }}
                                        >
                                            {file ? (
                                                <>
                                                    <FileIcon />
                                                    <span className="bulk-dropzone__title">{file.name}</span>
                                                    <span className="bulk-dropzone__hint">
                                                        {rowCountLabel
                                                            ? `${rowCountLabel} selected. Click or drop to replace.`
                                                            : "Click or drop to replace."}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <UploadIcon />
                                                    <span className="bulk-dropzone__title">
                                                        {rejectedFileName
                                                            ? "Choose a fixed file or drag it here"
                                                            : "Choose a file or drag it here"}
                                                    </span>
                                                    <span className="bulk-dropzone__hint">
                                                        {rejectedFileName
                                                            ? "Fix the spreadsheet, then choose it again."
                                                            : ".csv or .xlsx"}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {hasResults && (
                                        <div className="bulk-results">
                                            {createdLine && (
                                                <div className="bulk-result-banner bulk-result-banner--success" role="status">
                                                    <span className="bulk-result-banner__icon" aria-hidden="true">
                                                        ✓
                                                    </span>
                                                    <p>{createdLine}</p>
                                                </div>
                                            )}
                                            {!createdLine && uploadSummary && uploadSummary.createdCount === 0 && (
                                                <div className="bulk-result-banner bulk-result-banner--empty" role="status">
                                                    <p>No athletes were added.</p>
                                                </div>
                                            )}
                                            {skippedLine && (
                                                <div className="bulk-result-skip" role="status">
                                                    <div className="bulk-result-banner bulk-result-banner--warn">
                                                        <span className="bulk-result-banner__icon" aria-hidden="true">
                                                            !
                                                        </span>
                                                        <p>{skippedLine}</p>
                                                    </div>
                                                    <ul className="bulk-skip-list">
                                                        {visibleSkipped.map((issue) => (
                                                            <li key={issue.row}>
                                                                {formatSkippedRowLine(
                                                                    issue,
                                                                    cellValueForBulkIssue(fileMatrix, issue)
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <div className="bulk-skip-actions">
                                                        {skippedIssues.length > BULK_SKIPPED_PREVIEW_LIMIT && (
                                                            <button
                                                                type="button"
                                                                className="bulk-text-link"
                                                                onClick={() => setShowAllSkipped((openAll) => !openAll)}
                                                            >
                                                                {showAllSkipped
                                                                    ? "Show fewer"
                                                                    : `Show all ${skippedIssues.length}`}
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="bulk-text-link bulk-skip-actions__download"
                                                            onClick={handleDownloadSkipped}
                                                        >
                                                            Download skipped rows (.xlsx)
                                                        </button>
                                                    </div>
                                                    {skipDownloadError && (
                                                        <p className="bulk-skip-download-error" role="alert">
                                                            {skipDownloadError}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {validationSummary && validationSummary.length > 0 && (
                                        <div className="ad-alert ad-alert-error bulk-inline-alert" role="alert">
                                            <p className="bulk-inline-alert__title">
                                                We couldn&apos;t import this file yet:
                                            </p>
                                            {rejectedFileName && (
                                                <p className="bulk-inline-alert__file">{rejectedFileName}</p>
                                            )}
                                            <ul>
                                                {validationSummary.map((line, i) => (
                                                    <li key={i}>{line}</li>
                                                ))}
                                            </ul>
                                            <p className="bulk-inline-alert__hint">
                                                Fix the spreadsheet, then choose the file again below.
                                            </p>
                                        </div>
                                    )}

                                    {error &&
                                        !uploadSummary &&
                                        !(validationSummary && validationSummary.length > 0) && (
                                        <div className="ad-alert ad-alert-error bulk-inline-alert" role="alert">
                                            {error}
                                        </div>
                                    )}

                                    {saving && (
                                        <div className="bulk-progress" role="progressbar" aria-label="Upload progress">
                                            <div className="bulk-progress__bar bulk-progress__bar--indeterminate" />
                                        </div>
                                    )}
                                </div>
                            </li>
                        </ol>

                        {!hasResults && (
                            <p className="bulk-upload-footnote">
                                Valid rows are added even if others have errors — you&apos;ll see what was
                                skipped and why. New athletes get a welcome email with a temporary password.
                            </p>
                        )}

                        <div className="bulk-upload-actions">
                            {hasResults ? (
                                <button type="button" className="btn btn-red" onClick={handleClose} disabled={saving}>
                                    Done
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="bulk-ghost-btn"
                                        onClick={handleClose}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-red"
                                        disabled={saving || !file}
                                    >
                                        {saving ? "Uploading…" : "Upload athletes"}
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
